import { PromotionDeduction } from "@/core/entities/promotion/PromotionDeduction";
import { NumberFormatType } from "@/core/types";
import { ProgressChip } from "@/presentation/deduction/components/DeductionChips/ProgressChip";
import { DeductionUserDisplay } from "@/presentation/deduction/components/DeductionUserDisplay";
import { SkeletonCell } from "@/presentation/ui/components/common";
import { Avatar, Flex, Span, TableColumnType } from "@ir/ui";
import classNames from "classnames";
import { formatPromotionCurrency, formatPromotionDate } from "./promotionDisplay.utils";
import { MatchConfidenceBar } from "../components/MatchConfidenceBar";

export interface AppliedDeductionsTableColumnOptions {
  isRefreshing: boolean;
  dateFormat: string;
  numberFormat?: NumberFormatType;
}

export const createAppliedDeductionsTableColumns = ({
  isRefreshing,
  dateFormat,
  numberFormat,
}: AppliedDeductionsTableColumnOptions): TableColumnType<PromotionDeduction>[] => [
  {
    title: "Check No.",
    dataIndex: "deduction",
    key: "checkNumber",
    width: 165,
    verticalAlign: "top",
    fixed: "left",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={80} />;
      }
      return <Span>{record.deduction.checkNumber ?? "-"}</Span>;
    },
  },
  {
    title: "Invoice No.",
    dataIndex: "deduction",
    key: "invoiceNumber",
    width: 250,
    verticalAlign: "top",
    fixed: "left",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={80} />;
      }
      return <Span className="text-truncate">{record.deduction.invoiceNumber ?? "-"}</Span>;
    },
  },
  {
    title: "Invoice Date",
    dataIndex: "deduction",
    key: "invoiceDate",
    width: 120,
    verticalAlign: "top",
    hidden: true,
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={80} />;
      }
      return <Span>{formatPromotionDate(record.deduction.invoiceDate, dateFormat)}</Span>;
    },
  },
  {
    title: "Net Amount",
    dataIndex: "deduction",
    key: "amount",
    width: 100,
    align: "right",
    verticalAlign: "top",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={60} />;
      }
      return <Span>{formatPromotionCurrency(record.deduction.amount, numberFormat)}</Span>;
    },
  },
  {
    title: "Confidence",
    dataIndex: "matchSummary",
    key: "confidence",
    width: 140,
    verticalAlign: "top",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={100} />;
      }
      return <MatchConfidenceBar confidenceScore={record.matchSummary.confidenceScore} />;
    },
  },
  {
    title: "Progress",
    dataIndex: "deduction",
    key: "progress",
    width: 160,
    verticalAlign: "top",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell height={10} width={80} />;
      }
      const { stage, status } = record.deduction;
      if (!stage.stageKey || !stage.stageName || !status.statusKey || !status.statusName) {
        return null;
      }
      return (
        <ProgressChip
          stageKey={stage.stageKey}
          stageName={stage.stageName}
          statusKey={status.statusKey}
          statusName={status.statusName}
          withBorder={true}
          withBackground={true}
          shape="circle"
          size="medium"
        />
      );
    },
  },
  {
    title: "Analyst",
    dataIndex: "deduction",
    key: "analyst",
    width: 170,
    verticalAlign: "top",
    cellRender(_value: unknown, record: PromotionDeduction) {
      if (isRefreshing) {
        return <SkeletonCell />;
      }
      const analyst = record.deduction.analystUser;
      if (!analyst?.fullName) {
        return <Span>—</Span>;
      }
      return (
        <Flex align="center" className="display-user-wrapper" justifyContent="start" gap={0.5} wrap="no-wrap">
          <Avatar
            className={classNames("avatar-white", { "avatar-white-no-name": !analyst.fullName })}
            name={analyst.fullName}
            shape="circle"
            size="extra-small"
            isDeleteUser={analyst.isDeleted ?? undefined}
            title={analyst.isDeleted ? "Removed User" : analyst.fullName}
          />
          <DeductionUserDisplay name={analyst.fullName} />
        </Flex>
      );
    },
  },
];
